import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useFormDraft — LocalStorage-backed auto-save for any form modal.
 *
 *   const { restored, discardDraft, clearDraft } = useFormDraft({
 *     enabled: editing !== null,
 *     key: `reachvel:draft:${entityName}:${editing?.id || "new"}`,
 *     form, setForm,
 *   });
 *
 * Behaviour:
 * - When ``enabled`` flips true, we look for an existing draft under ``key``
 *   and (only if it's newer than the passed-in ``form`` seed) hydrate it back
 *   into state, setting ``restored=true`` so the modal can show a chip.
 * - Every subsequent ``form`` change writes to localStorage on a small debounce
 *   so a browser refresh mid-typing never loses the entry.
 * - Call ``clearDraft()`` after a successful save so we don't restore stale
 *   data next time.
 * - ``discardDraft()`` wipes the draft and resets ``restored`` — used by the
 *   "Discard draft" link.
 *
 * NOTE: Uses ``JSON.stringify`` — never store binary blobs or file objects
 * inside form state. For CR paste-images we're OK because they're stored as
 * base64 data-URLs inside the description HTML string.
 */
export function useFormDraft({ enabled, key, form, setForm, debounceMs = 350 }) {
  const [restored, setRestored] = useState(false);
  const restoredForKey = useRef("");
  const timer = useRef(null);

  // On enable / key change → try to restore
  useEffect(() => {
    if (!enabled || !key) { setRestored(false); return; }
    if (restoredForKey.current === key) return;   // already handled this key
    restoredForKey.current = key;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) { setRestored(false); return; }
      const draft = JSON.parse(raw);
      // Sanity: don't restore if the draft is identical to the seed (nothing to recover).
      if (JSON.stringify(draft) === JSON.stringify(form)) { setRestored(false); return; }
      setForm(draft);
      setRestored(true);
    } catch { setRestored(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  // Debounced write on every form change while modal is open
  useEffect(() => {
    if (!enabled || !key) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try { localStorage.setItem(key, JSON.stringify(form)); } catch { /* quota exceeded */ }
    }, debounceMs);
    return () => timer.current && clearTimeout(timer.current);
  }, [enabled, key, form, debounceMs]);

  const clearDraft = useCallback(() => {
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* noop */ }
    setRestored(false);
    restoredForKey.current = "";
  }, [key]);

  const discardDraft = useCallback(() => {
    clearDraft();
    // Caller can reset `form` externally; we just clear the storage + flag.
  }, [clearDraft]);

  return { restored, clearDraft, discardDraft };
}
